/**
 * 多选联合包围盒缩放：在世界坐标系下统一缩放，写回各元素文档坐标。
 */
import { useEffect, useRef, useState } from 'react';
import type { ElementPatch, Page } from '@/components/canvas/document/publicTypes';
import {
  scaleSnapshotToPatch,
  type MultiSelectionCoordinateSpace,
  type MultiSelectionSnapshot,
} from '@/components/canvas/selection/multiSelectionSnapshots';

export type { MultiSelectionSnapshot };
import { computeGroupRect } from './useGroupResize';
import { applyAspectRatio, type ResizeHandle } from './useResize';

export {
  buildDocumentSnapshots,
  unionBoundsFromSnapshots,
} from '@/components/canvas/selection/multiSelectionSnapshots';

export interface MultiSelectionResizeState {
  handle: ResizeHandle;
  startX: number;
  startY: number;
  startW: number;
  startH: number;
  startClientX: number;
  startClientY: number;
  snapshots: MultiSelectionSnapshot[];
  pages: Page[];
  /** 与联合框、union 计算使用同一坐标系 */
  coordinateSpace: MultiSelectionCoordinateSpace;
}

export function useMultiSelectionResize(
  zoom: number,
  pages: Page[],
  updateElementSilentInDocument: (id: string, patch: ElementPatch) => void,
  commitMultiSelectionResize: (patches: { id: string; patch: ElementPatch }[]) => void,
) {
  const [multiResizeState, setMultiResizeState] = useState<MultiSelectionResizeState | null>(null);
  const [multiResizePreview, setMultiResizePreview] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const multiResizePreviewRef = useRef<{ x: number; y: number; width: number; height: number } | null>(
    null,
  );
  const pagesRef = useRef(pages);
  pagesRef.current = pages;

  useEffect(() => {
    if (!multiResizeState) return;
    const {
      handle,
      startX,
      startY,
      startW,
      startH,
      startClientX,
      startClientY,
      snapshots,
      coordinateSpace,
    } = multiResizeState;

    const onMove = (e: MouseEvent) => {
      const dX = (e.clientX - startClientX) / zoom;
      const dY = (e.clientY - startClientY) / zoom;
      let newRect = computeGroupRect(handle, startX, startY, startW, startH, dX, dY);
      if (e.shiftKey) {
        const { x, y, w, h } = applyAspectRatio(
          handle,
          startW,
          startH,
          newRect.x,
          newRect.y,
          newRect.width,
          newRect.height,
        );
        newRect = { x, y, width: w, height: h };
      }
      const scaleX = newRect.width / startW;
      const scaleY = newRect.height / startH;
      const pageList = pagesRef.current;
      const unionStart = { x: startX, y: startY };
      const unionNew = { x: newRect.x, y: newRect.y };

      for (const snap of snapshots) {
        updateElementSilentInDocument(
          snap.id,
          scaleSnapshotToPatch(snap, pageList, unionStart, unionNew, scaleX, scaleY, coordinateSpace),
        );
      }

      multiResizePreviewRef.current = newRect;
      setMultiResizePreview(newRect);
    };

    const onUp = () => {
      const finalRect = multiResizePreviewRef.current;
      if (finalRect) {
        const scaleX = finalRect.width / startW;
        const scaleY = finalRect.height / startH;
        const pageList = pagesRef.current;
        const unionStart = { x: startX, y: startY };
        const unionNew = { x: finalRect.x, y: finalRect.y };
        const patches = snapshots.map((snap) => ({
          id: snap.id,
          patch: scaleSnapshotToPatch(
            snap,
            pageList,
            unionStart,
            unionNew,
            scaleX,
            scaleY,
            coordinateSpace,
          ),
        }));
        commitMultiSelectionResize(patches);
      }
      setMultiResizeState(null);
      setMultiResizePreview(null);
      multiResizePreviewRef.current = null;
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [multiResizeState, zoom, updateElementSilentInDocument, commitMultiSelectionResize]);

  return {
    multiResizeState,
    setMultiResizeState,
    multiResizePreview,
    setMultiResizePreview,
    multiResizePreviewRef,
  };
}
